import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/shared/Header';
import BottomNav from '@/components/shared/BottomNav';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import ExpenseItem from './ExpenseItem';
import PullToRefresh from '@/components/shared/PullToRefresh';
import { useExpenses } from '@/hooks/useExpenses';
import { useAppContext } from '@/context/AppContext';
import { formatCurrency, currentMonth } from '@/utils/formatters';
import { USERS } from '@/constants';

export default function ExpenseList() {
  const navigate   = useNavigate();
  const { selectedMonth, categories, paymentModes } = useAppContext();

  // Date mode: month picker vs custom range
  const [dateMode, setDateMode]   = useState('month');
  const [filterMonth, setFilterMonth] = useState(selectedMonth);
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');

  // Filters
  const [vendorSearch, setVendorSearch]   = useState('');
  const [selectedCat, setSelectedCat]     = useState('');
  const [selectedMode, setSelectedMode]   = useState('');
  const [selectedUser, setSelectedUser]   = useState('');

  // Amount range
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Sort
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir]     = useState('desc');

  // Selection mode
  const [selectMode, setSelectMode]   = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const activeFilters = useMemo(() => ({
    ...(dateMode === 'month'
      ? { month: filterMonth }
      : { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
    category:    selectedCat  || undefined,
    paymentMode: selectedMode || undefined,
    vendor:      vendorSearch || undefined,
  }), [dateMode, filterMonth, dateFrom, dateTo, selectedCat, selectedMode, vendorSearch]);

  const { expenses, loading, deleteExpense, refetch } = useExpenses(activeFilters);

  // Client-side: amount range filter + sort
  const displayedExpenses = useMemo(() => {
    let list = [...expenses];
    const min = minAmount !== '' ? Number(minAmount) : null;
    const max = maxAmount !== '' ? Number(maxAmount) : null;
    if (min !== null && !isNaN(min)) list = list.filter(e => e.amount >= min);
    if (max !== null && !isNaN(max)) list = list.filter(e => e.amount <= max);
    if (selectedUser) list = list.filter(e => e.createdBy === selectedUser);
    list.sort((a, b) => {
      const cmp = sortField === 'amount'
        ? a.amount - b.amount
        : a.date.localeCompare(b.date);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [expenses, minAmount, maxAmount, sortField, sortDir]);

  const filteredTotal = useMemo(
    () => displayedExpenses.reduce((s, e) => s + e.amount, 0),
    [displayedExpenses]
  );

  // Dynamic slider max
  const sliderMax = useMemo(
    () => Math.max(10000, ...expenses.map(e => e.amount)),
    [expenses]
  );

  function handleDelete(id) {
    if (confirm('Delete this expense?')) deleteExpense(id);
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === displayedExpenses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedExpenses.map(e => e.id)));
    }
  }

  async function handleDeleteSelected() {
    if (!selectedIds.size) return;
    if (!confirm(`Delete ${selectedIds.size} selected expense${selectedIds.size !== 1 ? 's' : ''}?`)) return;
    for (const id of selectedIds) {
      await deleteExpense(id);
    }
    setSelectedIds(new Set());
    setSelectMode(false);
  }

  function handleDownloadSelected() {
    const toExport = displayedExpenses.filter(e => selectedIds.has(e.id));
    if (!toExport.length) return;
    const label = dateMode === 'month' ? filterMonth : `${dateFrom}_${dateTo}`;
    const rows = [
      ['Date', 'Vendor', 'Category', 'Payment Mode', 'Amount (₹)', 'Notes'],
      ...toExport.map(e => [e.date, e.vendor ?? '', e.category ?? '', e.paymentMode ?? '', e.amount, e.comment ?? '']),
    ];
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: `NammaExpenses_${label}_selected.csv` });
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleDownload() {
    const label = dateMode === 'month' ? filterMonth : `${dateFrom}_${dateTo}`;
    const rows = [
      ['Date', 'Vendor', 'Category', 'Payment Mode', 'Amount (₹)', 'Notes'],
      ...displayedExpenses.map(e => [
        e.date, e.vendor ?? '', e.category ?? '', e.paymentMode ?? '', e.amount, e.comment ?? '',
      ]),
    ];
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: `NammaExpenses_${label}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  }

  const rightActions = (
    <button onClick={() => navigate('/expenses/add')} className="text-white text-xl font-bold" aria-label="Add expense">＋</button>
  );

  return (
    <div className="flex flex-col h-full">
      <Header title="📋 All Expenses" bgColor="bg-teal" subtitle={`${displayedExpenses.length} records`} rightActions={rightActions} />
      <div className="bg-white border-b border-gray-100 px-3 py-2 space-y-2 overflow-y-auto max-h-72">

        {/* Date mode toggle */}
        <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-lg text-xs">
          <button
            onClick={() => setDateMode('month')}
            className={`flex-1 py-1 rounded-md font-semibold transition-colors ${dateMode === 'month' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}
          >By Month</button>
          <button
            onClick={() => setDateMode('range')}
            className={`flex-1 py-1 rounded-md font-semibold transition-colors ${dateMode === 'range' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500'}`}
          >Custom Range</button>
        </div>

        {/* Date input */}
        {dateMode === 'month' ? (
          <input type="month" value={filterMonth} max={currentMonth()}
            onChange={e => setFilterMonth(e.target.value)}
            className="input-field w-full py-1.5 text-xs" aria-label="Filter by month" />
        ) : (
          <div className="flex gap-2">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="input-field flex-1 py-1.5 text-xs" aria-label="From date" placeholder="From" />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="input-field flex-1 py-1.5 text-xs" aria-label="To date" placeholder="To" />
          </div>
        )}

        {/* Vendor search */}
        <input type="text" value={vendorSearch} onChange={e => setVendorSearch(e.target.value)}
          placeholder="🔍 Search vendor…" className="input-field w-full py-1.5 text-xs" aria-label="Search by vendor" />

        {/* Category + payment mode + user */}
        <div className="flex gap-2">
          <select value={selectedCat} onChange={e => setSelectedCat(e.target.value)}
            className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white flex-1" aria-label="Filter by category">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
          </select>
          <select value={selectedMode} onChange={e => setSelectedMode(e.target.value)}
            className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white flex-1" aria-label="Filter by payment mode">
            <option value="">All Modes</option>
            {paymentModes.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>

        {/* User filter */}
        <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
          className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white w-full" aria-label="Filter by user">
          <option value="">All Users</option>
          {Object.values(USERS).map(u => (
            <option key={u.email} value={u.email}>{u.name}</option>
          ))}
        </select>

        {/* Amount range */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Amount Range (₹)</span>
            <span>{minAmount || 0} – {maxAmount || sliderMax}</span>
          </div>
          <input type="range" min={0} max={sliderMax} step={100}
            value={minAmount !== '' ? minAmount : 0}
            onChange={e => setMinAmount(e.target.value)}
            className="w-full accent-teal-600" aria-label="Minimum amount slider" />
          <input type="range" min={0} max={sliderMax} step={100}
            value={maxAmount !== '' ? maxAmount : sliderMax}
            onChange={e => setMaxAmount(e.target.value)}
            className="w-full accent-teal-600" aria-label="Maximum amount slider" />
          <div className="flex gap-2">
            <input type="number" value={minAmount} min={0} onChange={e => setMinAmount(e.target.value)}
              placeholder="Min ₹" className="input-field flex-1 py-1 text-xs" aria-label="Minimum amount" />
            <input type="number" value={maxAmount} min={0} onChange={e => setMaxAmount(e.target.value)}
              placeholder="Max ₹" className="input-field flex-1 py-1 text-xs" aria-label="Maximum amount" />
          </div>
        </div>

        {/* Sort */}
        <div className="flex gap-2 items-center">
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide flex-shrink-0">Sort:</span>
          <select value={sortField} onChange={e => setSortField(e.target.value)}
            className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white flex-1">
            <option value="date">Date</option>
            <option value="amount">Amount</option>
          </select>
          <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="text-xs border border-gray-200 rounded px-2 py-1 bg-white whitespace-nowrap"
            aria-label={`Toggle sort direction, currently ${sortDir}`}>
            {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="bg-teal px-4 py-2 flex justify-between items-center gap-2">
        <button
          onClick={() => { setSelectMode(m => !m); setSelectedIds(new Set()); }}
          className="text-white text-xs opacity-90 font-medium whitespace-nowrap"
          aria-label={selectMode ? 'Exit selection mode' : 'Enter selection mode'}
        >
          {selectMode ? '✕ Cancel' : '☑ Select'}
        </button>
        {selectMode ? (
          <div className="flex items-center gap-2 flex-1 justify-end">
            <button onClick={toggleSelectAll} className="text-white text-xs opacity-90 font-medium whitespace-nowrap">
              {selectedIds.size === displayedExpenses.length ? 'Deselect All' : 'Select All'}
            </button>
            <button onClick={handleDownloadSelected} disabled={!selectedIds.size} className="text-white text-xs opacity-90 font-medium whitespace-nowrap disabled:opacity-40" aria-label="Download selected">⬇ ({selectedIds.size})</button>
            <button onClick={handleDeleteSelected} disabled={!selectedIds.size} className="text-red-200 text-xs font-medium whitespace-nowrap disabled:opacity-40" aria-label="Delete selected">🗑 ({selectedIds.size})</button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <button onClick={handleDownload} className="text-white text-sm opacity-90 font-medium" aria-label="Download CSV" title="Download as CSV">⬇ CSV</button>
            <span className="text-sm font-bold text-white">{formatCurrency(filteredTotal)}</span>
          </div>
        )}
      </div>

      <PullToRefresh onRefresh={refetch} className="flex-1 overflow-y-auto scrollbar-none pb-20">
        {loading ? (
          <LoadingSpinner />
        ) : displayedExpenses.length ? (
          <div className="p-3 space-y-2">
            {displayedExpenses.map(e => (
              <ExpenseItem
                key={e.id}
                expense={e}
                onDelete={handleDelete}
                selectable={selectMode}
                selected={selectedIds.has(e.id)}
                onSelect={toggleSelect}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 gap-2">
            <span className="text-4xl">📋</span>
            <p className="text-gray-400 text-sm">No expenses found</p>
          </div>
        )}
      </PullToRefresh>

      {/* FAB */}
      <button className="fab" onClick={() => navigate('/expenses/add')} aria-label="Add new expense">＋</button>

      <BottomNav />
    </div>
  );
}

